import React, { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Calculator, Loader2, AlertTriangle } from "lucide-react";

/**
 * Calculadora — SÓ LEITURA (não grava, não gera doc, não toca estoque).
 *
 * Cartão único: "Gola e punho".
 * Composição da polo (FIXA — anatomia da peça): 1 polo = 1 gola + 2 punhos.
 * Rendimento físico do rolo (VARIÁVEL — vem do cadastro em Configurações).
 *
 * Fórmulas:
 *   Modo "juntos"  → peso = polos ÷ juntos_golas       (rolo único traz as duas peças)
 *                    qtd_golas  = peso × juntos_golas  (derivado do cadastro)
 *                    qtd_punhos = peso × juntos_punhos (derivado do cadastro)
 *   Modo "gola separado"  → peso = (polos × 1) ÷ gola_sep    (1 é composição da polo)
 *   Modo "punho separado" → peso = (polos × 2) ÷ punho_sep   (2 é composição da polo)
 *
 * Estrutura pronta pra receber cartões futuros abaixo (piquet, moletom, etc.)
 * seguindo o mesmo padrão: cabeçalho + input próprio + linhas compactas.
 */

const fmtKg = (v) => (v == null || Number.isNaN(v) ? "—" : `${v.toFixed(2).replace(".", ",")} kg`);
const fmtInt = (v) => {
  if (v == null || Number.isNaN(v)) return "—";
  // Se muito próximo de inteiro, mostra inteiro; senão 1 casa decimal
  const rounded = Math.round(v);
  if (Math.abs(v - rounded) < 0.05) return String(rounded);
  return v.toFixed(1).replace(".", ",");
};

export default function Calculadora() {
  const [rend, setRend] = useState(null); // {juntos_golas, juntos_punhos, gola_sep, punho_sep}
  const [loading, setLoading] = useState(true);
  const [polos, setPolos] = useState("");

  useEffect(() => {
    api.get("/rendimento-gola-punho")
      .then((r) => setRend(r.data || {}))
      .catch((e) => { const _m = apiError(e); if (_m) toast.error(_m); })
      .finally(() => setLoading(false));
  }, []);

  const polosNum = useMemo(() => {
    const n = parseFloat(String(polos).replace(",", "."));
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [polos]);

  const calc = useMemo(() => {
    if (!rend || polosNum == null) return null;
    const jg = rend.juntos_golas;
    const jp = rend.juntos_punhos;
    const gs = rend.gola_sep;
    const ps = rend.punho_sep;

    // Juntos: precisa juntos_golas pra ter peso. juntos_punhos é usado só na aux.
    const juntos = (jg && jg > 0)
      ? {
          peso: polosNum / jg,
          qtd_golas: (polosNum / jg) * jg, // = polos, mas derivado do cadastro
          qtd_punhos: (jp && jp > 0) ? (polosNum / jg) * jp : null,
        }
      : null;

    // Separados: composição da polo (1 e 2 fixos) — independentes de "juntos".
    const gola_sep = (gs && gs > 0) ? { peso: (polosNum * 1) / gs } : null;
    const punho_sep = (ps && ps > 0) ? { peso: (polosNum * 2) / ps } : null;

    return { juntos, gola_sep, punho_sep };
  }, [rend, polosNum]);

  const inputCls =
    "h-12 w-full rounded-md border-2 border-slate-300 px-3 text-base font-mono outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200";

  return (
    <div data-testid="calculadora-page" className="mx-auto max-w-3xl">
      <div className="mb-6">
        <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">
          <Calculator size={28} /> Calculadora
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Consulta de peso para separar aviamento. <strong>Não altera estoque, não gera documento.</strong>
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-20 text-slate-400"><Loader2 className="animate-spin" /></div>
      ) : (
        // Espaço para múltiplos cartões no futuro (piquet, moletom, etc.)
        <div className="space-y-6" data-testid="calc-cards">
          <CardGolaPunho
            polos={polos}
            setPolos={setPolos}
            polosNum={polosNum}
            calc={calc}
            rendVazio={
              !rend ||
              (rend.juntos_golas == null && rend.juntos_punhos == null && rend.gola_sep == null && rend.punho_sep == null)
            }
            inputCls={inputCls}
          />
        </div>
      )}
    </div>
  );
}

// ── Cartão único: Gola e Punho ───────────────────────────────────────
function CardGolaPunho({ polos, setPolos, polosNum, calc, rendVazio, inputCls }) {
  return (
    <section className="rounded-md border-2 border-slate-200 bg-white p-5" data-testid="calc-card-gola-punho">
      <h2 className="text-lg font-bold text-slate-900">Gola e punho</h2>
      <p className="mt-1 text-xs text-slate-500">
        Composição da polo (fixa): <strong>1 polo = 1 gola + 2 punhos</strong>.
      </p>

      {rendVazio && (
        <div className="mt-4 flex items-start gap-2 rounded-md border-2 border-amber-200 bg-amber-50 p-3 text-sm font-semibold text-amber-800" data-testid="calc-sem-cadastro">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>Nenhum rendimento cadastrado. Peça ao administrador para preencher em Configurações → Rendimento de gola e punho.</span>
        </div>
      )}

      <div className="mt-4">
        <label className="block max-w-xs">
          <span className="mb-1 block text-sm font-semibold text-slate-700">Quantas polos?</span>
          <input
            type="text"
            inputMode="numeric"
            className={inputCls}
            placeholder="Ex: 20"
            value={polos}
            onChange={(e) => setPolos(e.target.value.replace(/[^0-9.,]/g, ""))}
            data-testid="calc-polos"
          />
        </label>
      </div>

      {polos !== "" && polosNum == null && (
        <div className="mt-3 rounded-md border-2 border-red-200 bg-red-50 p-3 text-sm font-semibold text-red-700" data-testid="calc-erro-polos">
          Informe uma quantidade de polos maior que zero.
        </div>
      )}

      {polosNum != null && calc && (
        <div className="mt-4 divide-y-2 divide-slate-100 rounded-md border-2 border-slate-200 bg-slate-50" data-testid="calc-resultado">
          <LinhaResultado
            titulo="Gola e punho juntos"
            testid="linha-juntos"
            peso={calc.juntos?.peso}
            auxiliar={
              calc.juntos
                ? (calc.juntos.qtd_punhos != null
                    ? <>Peças: <strong>{fmtInt(calc.juntos.qtd_golas)} golas + {fmtInt(calc.juntos.qtd_punhos)} punhos</strong></>
                    : <>Peças: <strong>{fmtInt(calc.juntos.qtd_golas)} golas</strong> <span className="text-amber-700">(punhos: cadastro incompleto)</span></>)
                : null
            }
          />
          <LinhaResultado titulo="Gola separado" testid="linha-gola-sep" peso={calc.gola_sep?.peso} />
          <LinhaResultado titulo="Punho separado" testid="linha-punho-sep" peso={calc.punho_sep?.peso} />
        </div>
      )}
    </section>
  );
}

function LinhaResultado({ titulo, peso, auxiliar, testid }) {
  const cadastrado = peso != null && !Number.isNaN(peso);
  return (
    <div className="flex flex-col gap-1 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid={testid}>
      <div className="text-sm font-bold uppercase tracking-wider text-slate-600">{titulo}</div>
      <div className="flex flex-col items-start sm:items-end">
        {cadastrado ? (
          <>
            <div className="text-2xl font-bold font-mono text-slate-900" data-testid={`${testid}-peso`}>{fmtKg(peso)}</div>
            {auxiliar && (
              <div className="mt-0.5 text-xs text-slate-500" data-testid={`${testid}-aux`}>{auxiliar}</div>
            )}
          </>
        ) : (
          <div className="flex items-center gap-1 text-sm font-semibold text-amber-700" data-testid={`${testid}-warn`}>
            <AlertTriangle size={14} /> não cadastrado
          </div>
        )}
      </div>
    </div>
  );
}
